const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const actions = ['QUIZ_START', 'QUIZ_ANSWER', 'QUIZ_SIGN', 'QUIZ_CLAIM'];
const count = value => Number.isSafeInteger(value) && value >= 0;
const money = value => typeof value === 'string' && /^\d+\.\d{2}$/.test(value);
const privateKeys = new Set(['correctKey', 'questionData', 'validationData', 'rules', 'bankRevision']);
function publicOnly(value) {
  if (!value || typeof value !== 'object') return true;
  return Object.entries(value).every(([key, child]) => !privateKeys.has(key) && publicOnly(child));
}

// Validate the complete server projection before enabling any quiz action.
export function validEuroQuizState(state, activityId) {
  return state && String(state.activityId) === String(activityId) && typeof state.enabled === 'boolean' &&
    count(state.serverNow) && money(state.walletBalance) && publicOnly(state) &&
    Array.isArray(state.confirmedRequestIds) && state.confirmedRequestIds.every(value => uuid.test(value)) &&
    Array.isArray(state.offers) && state.offers.every(row => {
      if (!/^quiz-[1-9]\d*$/.test(row?.key) || typeof row.title !== 'string' || typeof row.canStart !== 'boolean' ||
          typeof row.canSign !== 'boolean' || typeof row.signedToday !== 'boolean' ||
          !['earned', 'used', 'usedToday', 'available', 'correctCount', 'streak', 'bestStreak', 'correctToday', 'signDays', 'seconds', 'dailyLimit'].every(key => count(row[key])) ||
          ![1, 2].includes(row.playType) || !Array.isArray(row.rewards)) return false;
      if (row.question && (!count(row.question.recordId) || !row.question.recordId || !count(row.question.deadline) ||
          !['PENDING', 'CORRECT', 'WRONG', 'EXPIRED'].includes(row.question.result) || typeof row.question.title !== 'string' ||
          !['', 'A', 'B', 'C', 'D'].includes(row.question.selectedKey) || !Array.isArray(row.question.options) || row.question.options.length !== 4 ||
          row.question.options.some((option, index) => option?.key !== 'ABCD'[index] || typeof option.title !== 'string'))) return false;
      if (!count(row.cycleId ?? 0) || !count(row.lastClaimId ?? 0) || !money(row.lastClaimAmount ?? '0.00')) return false;
      return row.rewards.every(reward => /^count-[1-9]\d*$/.test(reward?.key) && count(reward.required) && reward.required > 0 &&
        money(reward.amount) && typeof reward.canClaim === 'boolean' && typeof reward.claimed === 'boolean' && count(reward.recordId) &&
        (!reward.claimed || reward.recordId > 0 && !reward.canClaim));
    });
}

// Pending operations retain their original UUID; refresh/recovery only reads state.
export function createEuroQuizSession({ api, identity, token, storage, newRequestId, changed = () => {}, now = Date.now }) {
  let activityId, actor, openedToken, alive = true, version = 0, readVersion = 0;
  let value = { state: null, loading: false, busy: false, error: '', pending: null, clockOffset: 0 };
  const publish = patch => { value = { ...value, ...patch }; changed(value); };
  const storageKey = () => `euro-quiz:${actor}:${activityId}`;
  const current = sequence => alive && sequence === version && actor === identity() && openedToken === token();
  const readPending = () => {
    const saved = storage.get(storageKey());
    if (!saved) return null;
    if (!uuid.test(saved.requestId) || !actions.includes(saved.action) || !/^quiz-[1-9]\d*$/.test(saved.targetKey)) throw new Error('上次答题请求待核对，请保留原记录');
    if (!count(saved.cycleId ?? 0)) throw new Error('上次领奖周期待核对，请保留原记录');
    return saved;
  };
  function accept(state) {
    if (!validEuroQuizState(state, activityId)) throw new Error('答题状态暂不可用，请重新查询');
    let pending = readPending();
    if (pending && state.confirmedRequestIds.includes(pending.requestId)) { storage.remove(storageKey()); pending = null; }
    publish({ state, pending, clockOffset: state.serverNow * 1000 - now(), error: '' });
  }
  async function refresh() {
    if (!alive || !actor || !openedToken || value.busy) return;
    const sequence = version, request = ++readVersion;
    publish({ loading: true, error: '' });
    try { const state = await api.state(activityId); if (current(sequence) && request === readVersion) accept(state); }
    catch (error) { if (current(sequence) && request === readVersion) publish({ error: error.message || '答题状态读取失败' }); }
    finally { if (current(sequence) && request === readVersion) publish({ loading: false }); }
  }
  async function open(id) {
    version++; readVersion++; alive = true; activityId = id; actor = identity(); openedToken = token();
    value = { state: null, loading: false, busy: false, error: '', pending: null, clockOffset: 0 }; publish({});
    if (!actor || !openedToken) { publish({ error: '登录后查看答题资格与进度' }); return; }
    try { publish({ pending: readPending() }); } catch (error) { publish({ error: error.message }); return; }
    await refresh();
  }
  async function send(data) {
    if (!current(version) || value.busy || value.loading || !value.state) return;
    const sequence = version;
    try {
      const saved = readPending();
      if (saved && JSON.stringify(saved) !== JSON.stringify(data)) throw new Error('请先核对上一次答题操作');
      storage.set(storageKey(), data); publish({ busy: true, pending: data, error: '' });
      const state = await api.action(activityId, data);
      if (!current(sequence)) return;
      accept(state);
      // A repeated start may restore the same active question without creating a new receipt.
      if (readPending()?.requestId === data.requestId) storage.remove(storageKey());
      publish({ pending: readPending() });
    } catch (error) { if (current(sequence)) publish({ error: error.message || '操作结果待确认，请先查询原结果' }); }
    finally { if (current(sequence)) publish({ busy: false }); }
  }
  async function execute(action, targetKey, { optionKey = '', questionId = 0, rewardKey = '' } = {}) {
    if (!actions.includes(action) || value.pending || value.error || value.busy || value.loading) return;
    const offer = value.state?.offers.find(row => row.key === targetKey);
    if (!offer) return;
    if (action === 'QUIZ_START' && !offer.canStart || action === 'QUIZ_SIGN' && !offer.canSign ||
        action === 'QUIZ_CLAIM' && !offer.rewards.some(row => row.key === rewardKey && row.canClaim) ||
        action === 'QUIZ_ANSWER' && (offer.question?.recordId !== questionId || offer.question.result !== 'PENDING' ||
          !['A', 'B', 'C', 'D'].includes(optionKey) || now() + value.clockOffset >= offer.question.deadline * 1000)) return;
    const requestId = newRequestId(); if (!uuid.test(requestId)) throw new Error('当前浏览器无法生成请求编号');
    return send({ action, targetKey, optionKey, questionId, rewardKey, requestId, cycleId: action === 'QUIZ_CLAIM' ? (offer.cycleId || 0) : 0 });
  }
  return { open, refresh, execute, retry: () => value.pending && send(value.pending), dispose() { alive = false; version++; readVersion++; } };
}
